import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from '@/providers/RouterShim';
import Container from '../Container';
import { IconCancel } from '../Icon';
import { Link, RouterLink } from '../Link';
import { SrOnly } from '../SrOnly';
import { Stack } from '../Stack';
import { Text } from '../Text';
import { Bar, CloseBtn, Main, Wrapper } from './wrapper';

// Utility to parse Markdown links and render as React elements
const parseMarkdownLinks = (branchId: string | undefined, message: string) => {
  const markdownLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
  const parts = [];
  let lastIndex = 0;

  message.replace(markdownLinkRegex, (match, text, url, offset) => {
    if (lastIndex < offset) {
      parts.push(
        <React.Fragment key={lastIndex}>{message.substring(lastIndex, offset)}</React.Fragment>,
      );
    }

    const parsedUrl = new URL(url);
    if (parsedUrl.hostname === window.location.hostname) {
      const fixedPathname = parsedUrl.pathname.replace(':id', branchId || '');
      parts.push(
        <RouterLink key={offset} to={fixedPathname} title={text}>
          {text}
        </RouterLink>,
      );
    } else {
      parts.push(
        <Link key={offset} href={url} title={text} target="_blank" rel="noopener noreferrer">
          {text}
        </Link>,
      );
    }

    lastIndex = offset + match.length;
    return '';
  });

  if (lastIndex < message.length) {
    parts.push(<React.Fragment key={lastIndex}>{message.substring(lastIndex)}</React.Fragment>);
  }

  return parts;
};

export type IncidentAlertToneProps = 'error' | 'warning' | 'info' | 'success';

interface Banner {
  id: string;
  heading?: string;
  message: string;
  tone?: IncidentAlertToneProps;
}

interface IncidentAlertProps {
  banners: Banner[];
  onRemove: (id: string) => void;
}

const IncidentAlert = ({ banners, onRemove }: IncidentAlertProps) => {
  const { t } = useTranslation('common');
  const { branchId } = useParams();

  return (
    <Wrapper>
      <Container>
        {banners.map(({ id, tone, heading, message }) => (
          <Stack key={id}>
            <Bar $tone={tone}>
              <Main>
                {heading && (
                  <Text $weight="bold" as="span">
                    {heading}
                  </Text>
                )}
                {parseMarkdownLinks(branchId, message)}
              </Main>
              <CloseBtn onClick={() => onRemove(id)} title={t('close')}>
                <IconCancel />
                <SrOnly>{t('close')}</SrOnly>
              </CloseBtn>
            </Bar>
          </Stack>
        ))}
      </Container>
    </Wrapper>
  );
};

export default IncidentAlert;
